/**
 * タイムラインの絞り込み
 *
 * 地図の絞り込みバーと同じ操作でプランの行を絞る。
 * 経路そのものは変えないので、絞り込み中は並べ替えを止めて番号はルートの位置を出す。
 */

import type { RoutePlanItem } from '@/api/types/route-plan'
import type { SortOption } from '@/components/shared/map/rally-filter-bar'
import { matchesStampFilter, type StampFilter } from '@/components/shared/map/stamp-filter'
import type { RallyLegendEntry } from '@/components/shared/map/use-rally-places'
import { calculateDistance } from '@/lib/geo-utils'

import { hasRally } from '../lib'

export type TimelineSort = 'route' | 'distance'

export const TIMELINE_SORT_OPTIONS: SortOption[] = [
  { value: 'route', label: 'ルートの順' },
  { value: 'distance', label: '現在地から近い順' },
]

export const isTimelineSort = (value: string): value is TimelineSort => value === 'route' || value === 'distance'

export type TimelineFilters = {
  stampFilter: StampFilter
  hiddenRallyIds: number[]
  prefectures: string[]
}

/** プランに入っているラリー。同じ場所が複数のラリーに属すので場所ではなく紐付きを数える */
export const legendOf = (items: RoutePlanItem[]): RallyLegendEntry[] => {
  const byId = new Map<number, RallyLegendEntry>()
  for (const item of items) {
    for (const link of item.rally_items) {
      const found = byId.get(link.rally_id)
      if (found) {
        found.count += 1
        continue
      }
      byId.set(link.rally_id, {
        rally: { id: link.rally_id, title: link.rally_title, color: link.rally_color },
        count: 1,
      })
    }
  }
  return [...byId.values()]
}

export const prefecturesOf = (items: RoutePlanItem[]): string[] => {
  const found = new Set<string>()
  for (const item of items) {
    if (item.prefecture) found.add(item.prefecture)
  }
  return [...found].sort((a, b) => a.localeCompare(b, 'ja'))
}

export const isFiltering = ({ stampFilter, hiddenRallyIds, prefectures }: TimelineFilters): boolean =>
  stampFilter !== 'all' || hiddenRallyIds.length > 0 || prefectures.length > 0

const matches = (item: RoutePlanItem, { stampFilter, hiddenRallyIds, prefectures }: TimelineFilters): boolean => {
  if (prefectures.length > 0 && (item.prefecture === null || !prefectures.includes(item.prefecture))) return false
  // ラリー外の場所はスタンプの概念がないので、獲得状況で絞るときは外す
  if (!hasRally(item)) return stampFilter === 'all'
  return item.rally_items.some(
    (link) => !hiddenRallyIds.includes(link.rally_id) && matchesStampFilter(link.stamped_at, stampFilter),
  )
}

export const filterItems = (items: RoutePlanItem[], filters: TimelineFilters): RoutePlanItem[] =>
  isFiltering(filters) ? items.filter((item) => matches(item, filters)) : items

/** 現在地が取れないうちはルートの順のまま。座標のない行は末尾へ回す */
export const sortByDistance = (
  items: RoutePlanItem[],
  origin: { latitude: number; longitude: number } | null,
): RoutePlanItem[] => {
  if (origin === null) return items
  const distanceOf = (item: RoutePlanItem): number =>
    item.latitude === null || item.longitude === null
      ? Number.POSITIVE_INFINITY
      : calculateDistance(origin.latitude, origin.longitude, item.latitude, item.longitude)
  return [...items].sort((a, b) => distanceOf(a) - distanceOf(b))
}
